import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';

function formatTierPrice(cents, currency) {
  const cur = (currency || 'nok').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0) / 100);
  } catch {
    return `${(cents || 0) / 100} ${cur}`;
  }
}

const CoachesPage = () => {
  const { t } = useLanguage();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/coaches');
        if (!cancelled) {
          setCoaches(res.data?.coaches || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || t('coaches.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-[240px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='max-w-5xl mx-auto p-6 space-y-6'>
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>{t('coaches.title')}</h1>
        <p className='text-sm text-muted-foreground mt-1'>{t('coaches.subtitle')}</p>
      </div>

      {error && (
        <p className='text-sm text-destructive' role='alert'>
          {error}
        </p>
      )}

      {!error && coaches.length === 0 && (
        <p className='text-sm text-muted-foreground'>{t('coaches.empty')}</p>
      )}

      <div className='grid gap-4 sm:grid-cols-2'>
        {coaches.map((c) => (
          <Link key={c.id} to={`/coaches/${c.id}`} className='block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
            <Card className='h-full transition-colors hover:bg-accent/40 hover:border-primary/30'>
              <CardHeader className='flex flex-row items-start gap-4 space-y-0'>
                <Avatar className='h-14 w-14 shrink-0'>
                  {c.avatarUrl ? <AvatarImage src={c.avatarUrl} alt='' /> : null}
                  <AvatarFallback className='bg-primary text-primary-foreground text-lg'>
                    {c.name?.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className='min-w-0'>
                  <CardTitle className='text-lg leading-tight'>{c.name}</CardTitle>
                  {c.headline && (
                    <CardDescription className='text-foreground/80 mt-1'>{c.headline}</CardDescription>
                  )}
                </div>
              </CardHeader>
              <CardContent className='pt-0'>
                {c.bioPreview && (
                  <p className='text-sm text-muted-foreground line-clamp-3'>{c.bioPreview}</p>
                )}
                {c.multipleTiers && (
                  <Badge variant='secondary' className='mt-2 text-xs font-normal'>
                    {t('coaches.multiplePlans')}
                  </Badge>
                )}
                {c.tier && (
                  <p className='text-xs text-muted-foreground mt-2'>
                    {t('coaches.fromPrice', {
                      month: formatTierPrice(c.tier.amountCentsMonth, c.tier.currency),
                      year: formatTierPrice(c.tier.amountCentsYear, c.tier.currency),
                    })}
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default CoachesPage;
